import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import { useExternalLink } from "@/contexts/ExternalLinkContext";
import { languageFlags, type Language } from "@/i18n/translations";

import logoImg from "@/assets/chibi-logo.webp";

const RESERVE_URL = "https://www.foodbooking.com/ordering/?restaurant_uid=b1c06bc6-d5a3-43d4-82d8-10720b240538&reservation=true";

const navLinks = [
  { to: "/", key: "nav_home" },
  { to: "/menu", key: "nav_menu" },
  { to: "/galeria", key: "nav_gallery" },
  { to: "/nosotros", key: "nav_about" },
  { to: RESERVE_URL, key: "nav_reservations", external: true },
  { to: "/contacto", key: "nav_contact" },
  { to: "/blog", key: "nav_blog" },
];

const languages: Language[] = ["es", "en", "ca", "pt", "fr", "it", "de", "ru"];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { pathname } = useLocation();
  const { lang, setLang, t } = useLanguage();
  const { openLink } = useExternalLink();

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-kuro/90 backdrop-blur-md border-b border-shiro/10">
        <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 py-2 md:py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img src={logoImg} alt="Chibikokoro" className="h-10 md:h-12 w-auto" width={48} height={48} />
            <div className="flex flex-col items-start">
              <span className="font-display text-lg md:text-xl font-semibold text-shiro tracking-wider leading-tight">
                <span className="text-aka">CHIBI</span><span className="text-shiro/70">KOKORO</span>
              </span>
              <span className="text-[9px] md:text-[10px] tracking-[0.2em] text-shiro/40 font-body italic">
                Japanese Restaurant
              </span>
            </div>
          </Link>
          {/* Desktop links */}
          <ul className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <li key={l.to}>
                {l.external ? (
                  <button
                    onClick={() => openLink(l.to)}
                    className="text-sm tracking-widest uppercase font-body transition-colors duration-300 text-shiro/70 hover:text-shiro"
                  >
                    {t(l.key)}
                  </button>
                ) : (
                  <Link
                    to={l.to}
                    className={`text-sm tracking-widest uppercase font-body transition-colors duration-300 ${
                      pathname === l.to
                        ? "text-aka"
                        : "text-shiro/70 hover:text-shiro"
                    }`}
                  >
                    {t(l.key)}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* Desktop CTA + Language */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language toggle */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 text-shiro/60 hover:text-shiro transition-colors text-sm font-body"
                aria-label="Change language"
              >
                <Globe className="w-4 h-4" />
                <span className="text-xs uppercase">{lang}</span>
              </button>
              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="absolute top-full right-0 mt-2 bg-kuro/95 backdrop-blur-lg border border-shiro/10 py-2 min-w-[140px]"
                  >
                    {languages.map((l) => (
                      <button
                        key={l}
                        onClick={() => { setLang(l); setLangOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-sm font-body flex items-center gap-2 hover:bg-shiro/10 transition-colors ${
                          lang === l ? "text-aka" : "text-shiro/70"
                        }`}
                      >
                        <span>{languageFlags[l]}</span>
                        <span className="uppercase text-xs">{l}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <a
              href="tel:+34931463045"
              className="text-shiro/60 hover:text-shiro transition-colors"
              aria-label={t("cta_call")}
            >
              <Phone className="w-4 h-4" />
            </a>
            <button
              onClick={() => openLink(RESERVE_URL)}
              className="bg-primary text-primary-foreground text-xs tracking-widest uppercase px-6 py-2.5 hover:bg-primary/90 transition-colors"
            >
              {t("cta_reserve")}
            </button>
          </div>

          {/* Mobile toggle */}
          <div className="flex items-center gap-3 lg:hidden">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="text-shiro/60 hover:text-shiro p-1"
              aria-label="Change language"
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              onClick={() => setOpen(!open)}
              className="text-shiro p-2"
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>
      </div>

      {/* Language dropdown mobile */}
      <AnimatePresence>
        {langOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-kuro/95 backdrop-blur-lg border-b border-shiro/10"
          >
            <div className="flex flex-wrap justify-center gap-2 py-4 px-5">
              {languages.map((l) => (
                <button
                  key={l}
                  onClick={() => { setLang(l); setLangOpen(false); }}
                  className={`px-3 py-1.5 text-xs font-body flex items-center gap-1.5 transition-colors ${
                    lang === l ? "text-aka bg-shiro/10" : "text-shiro/60 hover:text-shiro"
                  }`}
                >
                  <span>{languageFlags[l]}</span>
                  <span className="uppercase">{l}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-kuro/95 backdrop-blur-lg border-b border-shiro/10"
          >
            <ul className="flex flex-col items-center py-8 gap-6">
              {navLinks.map((l) => (
                <li key={l.to}>
                  {l.external ? (
                    <button
                      onClick={() => { openLink(l.to); setOpen(false); }}
                      className="text-sm tracking-widest uppercase font-body text-shiro/70"
                    >
                      {t(l.key)}
                    </button>
                  ) : (
                    <Link
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className={`text-sm tracking-widest uppercase font-body ${
                        pathname === l.to ? "text-aka" : "text-shiro/70"
                      }`}
                    >
                      {t(l.key)}
                    </Link>
                  )}
                </li>
              ))}
              <li>
                <button
                  onClick={() => { openLink(RESERVE_URL); setOpen(false); }}
                  className="bg-primary text-primary-foreground text-xs tracking-widest uppercase px-8 py-3 mt-2 inline-block"
                >
                  {t("cta_reserve_table")}
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
